// React and Material-UI imports
import { useEffect, useState, useMemo, useRef } from 'react';
import { Select, MenuItem, Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from 'react-redux';

// Child components
import { ShiftInformationContainer, RoleSelection, StyledIconButton } from './ShiftInformation.styles';
import EmployeeSelectionList from '../EmployeeSelectionList/EmployeeSelectionList';
import Notes from '../Notes/Notes';
import ShiftFinanceDetails from './components/ShiftFinanceDetails/ShiftFinanceDetails';
import EmployeeAssignmentButton from './components/EmployeeAssignmentButton';
import ShiftTimesContainer from './components/ShiftTimesContainer';
import ShiftActionsSection from './components/ShiftActionsSection';
import ShiftSwapStatus from './components/ShiftSwapStatus/ShiftSwapStatus';
import { ConflictList } from './components/ConflictWarningModal/ConflictList';

// Custom hooks for shift management
import { useShiftCostCalculation } from '../../utils/Hooks/shifts/useShiftCostCalculation';
import { useBreakManagement } from '../../utils/Hooks/shifts/useBreakManagement';
import { useShiftUpdater } from '../../utils/Hooks/shifts/useShiftUpdater';
import { useEmployeeAssignment } from '../../utils/Hooks/shifts/useEmployeeAssignment';

// Utilities and helpers
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

// Redux selectors
import { selectSelectedShift, selectUpdatedStatus } from '../../redux/shifts/shiftsSlice';
import { selectShiftsByAssignment } from '../../redux/shifts/shiftsSelectors';
import { selectAllShiftTypes } from '../../redux/slices/shiftTypesSlice';
import { selectHasPrivilegedRole } from '../../redux/slices/currentUserSlice';

/**
 * ShiftInformation displays and manages all aspects of a shift:
 * - Employee assignment
 * - Financial details (managers only)
 * - Notes
 * - Timing
 * - Actions (varies by role)
 *   - Managers: assign, delete, report
 *   - Employees: swap shift
 */
const ShiftInformation = ({
  isNewShift,
  handleCloseDrawer,
  handleBackNavigation = () => handleCloseDrawer(),
  shouldNavigateBackOnDelete = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // Core data and state initialization
  const shift = useSelector(selectSelectedShift);
  const updatedStatus = useSelector(selectUpdatedStatus);
  const shiftsByAssignment = useSelector(selectShiftsByAssignment);
  const shiftTypes = useSelector(selectAllShiftTypes);
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);

  const employees = useSelector((state) => state.employees.employees);
  const currentEmployee = useMemo(() => {
    if (!shift?.eid || !employees?.length) return null;
    return employees.find((emp) => emp.id === shift.eid);
  }, [employees, shift?.eid]);

  // Derived state
  const isAssigned = !!shift?.assignedName;
  const isShiftClockedIn = !!shift?.clockedInAt;
  const isShiftScheduledStartPassed = shift?.start < new Date();
  const isShiftDisabled = isShiftClockedIn || isShiftScheduledStartPassed;

  // Initialize hooks
  const calculateShiftCost = useShiftCostCalculation(shiftTypes);
  const handleShiftUpdate = useShiftUpdater(shift, calculateShiftCost);
  const {
    isSelectingEmployee,
    handleEmployeeSelect,
    handleStartEmployeeSelection,
    handleUnassignButtonClick,
    handleCancelEmployeeSelection,
  } = useEmployeeAssignment(handleShiftUpdate);
  useBreakManagement(isNewShift, shift, updatedStatus);

  // Track previous status to avoid logging on initial render
  const prevStatus = useRef(updatedStatus);
  const prevType = useRef(shift?.name);
  const prevId = useRef(shift?.id);

  useEffect(() => {
    if (!shift) return; // Don't process effects if shift is null

    if (prevStatus.current !== 'succeeded' && updatedStatus === 'succeeded') {
      showSuccessToast(
        prevType.current !== shift.name ? 'Shift type changed successfully' : 'Shift updated successfully'
      );
    }
    prevType.current = shift.name;
    prevStatus.current = updatedStatus;
  }, [shift, updatedStatus]);

  useEffect(() => {
    if (prevId.current != null && prevId.current !== shift?.id && isSelectingEmployee) handleCancelEmployeeSelection();
    prevId.current = shift?.id;
  }, [shift?.id, isSelectingEmployee, handleCancelEmployeeSelection]);

  // Handle going back from employee selection
  const handleBackFromEmployeeSelection = () => {
    handleCancelEmployeeSelection();
  };

  // Handle unassigning employee from the employee selection list
  const handleUnassignFromSelection = () => {
    handleUnassignButtonClick();
    // The selection mode will automatically close after unassigning
  };

  // Handle shift type changes
  const changeShiftType = (newType) => {
    if (newType === shift.name) {
      setIsEditing(false);
      return; // No change needed
    }

    // Check if employee is compatible with new shift type
    if (currentEmployee && !currentEmployee.shiftTypes.includes(newType)) {
      showErrorToast(
        `${currentEmployee.displayName} cannot be set as ${newType} (${currentEmployee.shiftTypes.join(', ')})`
      );
      // Unassign employee since they can't work this type of shift
      handleShiftUpdate({
        name: newType,
        assignedName: null,
        eid: null,
        assignedProfileImageUrl: null,
      });
    } else {
      // Just update the shift type
      handleShiftUpdate({ name: newType });
    }
    setIsEditing(false);
  };

  if (!shift) {
    return <Typography variant="body1">Shift data is unavailable</Typography>;
  }

  return (
    <ShiftInformationContainer>
      {!isEditing ? (
        <RoleSelection>
          <Typography variant="h4">{shift.name}</Typography>
          {hasPrivilegedRole && !isShiftDisabled && (
            <StyledIconButton onClick={() => setIsEditing(true)}>
              <EditIcon />
            </StyledIconButton>
          )}
        </RoleSelection>
      ) : (
        <Select value={shift.name} onChange={(e) => changeShiftType(e.target.value)} fullWidth>
          {shiftTypes.map((option) => (
            <MenuItem key={option.id} value={option.name}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      )}

      {isSelectingEmployee && hasPrivilegedRole ? ( // Show employee selection list only for managers
        <EmployeeSelectionList
          onSelect={handleEmployeeSelect}
          shiftType={shift.name || ''}
          shiftTime={[shift.start, shift.end]}
          shiftsByAssignment={shiftsByAssignment}
          onBack={handleBackFromEmployeeSelection}
          onUnassign={isAssigned ? handleUnassignFromSelection : null}
        />
      ) : (
        // Show shift details by default
        <>
          {hasPrivilegedRole && (
            <EmployeeAssignmentButton onAssignClick={handleStartEmployeeSelection} disabled={isShiftDisabled} />
          )}

          {isAssigned && hasPrivilegedRole && <ShiftFinanceDetails shift={shift} isInDayView={false} />}

          {/* Show shift swap status if there's a pending request */}
          <ShiftSwapStatus shift={shift} />

          <Notes
            disabled={isShiftDisabled}
            title="Shift Notes"
            placeholder={isShiftDisabled ? 'No notes for this shift' : 'Add notes visible to everyone'}
          />

          <ShiftTimesContainer shift={shift} handleShiftUpdate={handleShiftUpdate} disabled={isShiftDisabled} />

          {shift.conflicts?.length > 0 && (
            <div>
              <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                Shift Conflicts
                <WarningIcon fontSize="small" />
              </Typography>
              <ConflictList conflicts={shift.conflicts} />
            </div>
          )}

          <ShiftActionsSection
            handleCloseDrawer={handleCloseDrawer}
            handleBackNavigation={handleBackNavigation}
            handleShiftUpdate={handleShiftUpdate}
            handleStartEmployeeSelection={handleStartEmployeeSelection}
            disabled={isShiftDisabled}
            shouldNavigateBackOnDelete={shouldNavigateBackOnDelete}
          />
        </>
      )}
    </ShiftInformationContainer>
  );
};

export default ShiftInformation;
